import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { API_ENDPOINTS } from '../config';
import { IconAlert, IconChart } from '../components/Icons';
import './AdminFeedback.css';
import './AdminMetricsHistory.css';
import { getBearerAuthHeaders } from '../utils/authFetchHeaders';

const WINDOW_LABELS = { all: 'All Time', ytd: 'YTD', '1y': '1 Year', mtd: 'MTD' };

function fmtPct(v) {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  const n = Number(v);
  return `${n > 0 ? '+' : ''}${n.toFixed(2)}%`;
}

function fmtMoney(v) {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  const n = Number(v);
  return `${n < 0 ? '-' : ''}$${Math.abs(n).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

function toneClass(v) {
  const n = Number(v);
  if (!Number.isFinite(n) || n === 0) return '';
  return n > 0 ? 'pos' : 'neg';
}

function fmtDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
}

function alertHeadline(alert) {
  const d = alert.details || {};
  if (alert.kind === 'drift') {
    return `History rewritten: equity as of ${d.asOfDate} changed ${fmtMoney(d.deltaDollars)} `
      + `(${fmtMoney(d.equityWas)} → ${fmtMoney(d.equityNow)}) since the ${d.previousSnapshotDate} snapshot`;
  }
  if (alert.kind === 'action_impact') {
    const delta = d.deltas?.fixedReturnPct;
    return `${d.action || 'Edit'} moved total return by ${fmtPct(delta)} (${d.actorEmail || 'unknown admin'})`;
  }
  return alert.kind;
}

function AdminMetricsHistory() {
  const [strategies, setStrategies] = useState([]);
  const [strategy, setStrategy] = useState('');
  const [windowKey, setWindowKey] = useState('all');
  const [snapshots, setSnapshots] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [running, setRunning] = useState(false);
  const [message, setMessage] = useState('');
  const [diffFrom, setDiffFrom] = useState('');
  const [diffTo, setDiffTo] = useState('');
  const [diff, setDiff] = useState(null);
  const [diffLoading, setDiffLoading] = useState(false);

  // Shared helper: carries the active-entity header as well as the token.
  const authHeaders = () => getBearerAuthHeaders();

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { window: windowKey, limit: 120 };
      if (strategy) params.strategy = strategy;
      const [snapRes, alertRes] = await Promise.all([
        fetch(API_ENDPOINTS.adminMetricSnapshots(params), { headers: authHeaders() }),
        fetch(API_ENDPOINTS.adminMetricAlerts({ limit: 25 }), { headers: authHeaders() }),
      ]);
      const snapData = await snapRes.json();
      const alertData = await alertRes.json();
      if (!snapRes.ok || !snapData.success) throw new Error(snapData.error || 'Failed to load snapshots');
      setStrategies(snapData.strategies || []);
      setSnapshots(snapData.snapshots || []);
      if (alertData.success) setAlerts(alertData.alerts || []);
      if (!strategy && (snapData.strategies || []).length) {
        setStrategy(snapData.strategies[0].key);
      }
    } catch (err) {
      setError(err.message);
      setSnapshots([]);
    } finally {
      setLoading(false);
    }
  }, [strategy, windowKey]);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => { setDiff(null); setDiffFrom(''); setDiffTo(''); }, [strategy]);

  const snapshotDates = useMemo(() => snapshots.map((s) => s.snapshot_date), [snapshots]);

  const runSnapshotNow = async () => {
    setRunning(true);
    setMessage('');
    try {
      const res = await fetch(API_ENDPOINTS.adminMetricSnapshotsRun, {
        method: 'POST',
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Snapshot failed');
      setMessage(`Snapshot taken: ${data.rowsSaved} rows for ${data.strategiesSeen} strategies (${data.snapshotDate}).`);
      await fetchAll();
    } catch (err) {
      setMessage(`Snapshot failed: ${err.message}`);
    } finally {
      setRunning(false);
    }
  };

  const loadDiff = async () => {
    if (!strategy || !diffFrom || !diffTo) return;
    setDiffLoading(true);
    try {
      const res = await fetch(
        API_ENDPOINTS.adminMetricSnapshotsDiff({ strategy, from: diffFrom, to: diffTo }),
        { headers: authHeaders() },
      );
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Diff failed');
      setDiff(data);
    } catch (err) {
      setMessage(`Compare failed: ${err.message}`);
      setDiff(null);
    } finally {
      setDiffLoading(false);
    }
  };

  const ackAlert = async (id) => {
    try {
      await fetch(API_ENDPOINTS.adminMetricAlertById(id), { method: 'PATCH', headers: authHeaders() });
      setAlerts((prev) => prev.filter((a) => a.id !== id));
    } catch (_e) { /* refresh will restore it */ }
  };

  const activeStrategyName = strategies.find((s) => s.key === strategy)?.name || strategy;

  const renderDiffRow = (label, get, fmt = fmtPct) => {
    const fromRow = diff?.byDate?.[diff.from]?.[windowKey];
    const toRow = diff?.byDate?.[diff.to]?.[windowKey];
    const a = fromRow ? get(fromRow) : null;
    const b = toRow ? get(toRow) : null;
    const delta = (a != null && b != null) ? Number(b) - Number(a) : null;
    return (
      <tr key={label}>
        <td>{label}</td>
        <td className="num">{a != null ? fmt(a) : '—'}</td>
        <td className="num">{b != null ? fmt(b) : '—'}</td>
        <td className={`num ${toneClass(delta)}`}>{delta != null ? fmt(delta) : '—'}</td>
      </tr>
    );
  };

  return (
    <div className="admin-feedback-page admin-metrics-page">
      <div className="admin-feedback-content">
        <div className="admin-feedback-header">
          <h1 className="admin-feedback-title">
            <IconChart size={24} />
            Metrics History
          </h1>
          <div className="admin-feedback-filters">
            <select
              className="admin-feedback-select"
              value={strategy}
              onChange={(e) => setStrategy(e.target.value)}
              aria-label="Strategy"
            >
              {strategies.map((s) => (
                <option key={s.key} value={s.key}>{s.name}</option>
              ))}
            </select>
            <select
              className="admin-feedback-select"
              value={windowKey}
              onChange={(e) => setWindowKey(e.target.value)}
              aria-label="Window"
            >
              {Object.entries(WINDOW_LABELS).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
            <button
              type="button"
              className="admin-feedback-bulk-btn"
              onClick={runSnapshotNow}
              disabled={running}
            >
              {running ? 'Snapshotting…' : 'Snapshot now'}
            </button>
          </div>
        </div>

        <p className="admin-logs-intro">
          Daily immutable snapshots of every strategy's headline metrics, taken at 5:30 PM ET from the
          same data the dashboard serves. Use them to answer “what did the site say on date X” and to
          spot rewritten history.
        </p>

        {message ? <p className="admin-metrics-message">{message}</p> : null}
        {error ? (
          <div className="admin-feedback-error"><IconAlert size={18} />{error}</div>
        ) : null}

        {alerts.length > 0 && (
          <div className="admin-metrics-alerts">
            <h2>⚠ Unacknowledged alerts</h2>
            {alerts.map((a) => (
              <div key={a.id} className={`admin-metrics-alert admin-metrics-alert-${a.kind}`}>
                <div>
                  <strong>{a.strategy_name}</strong>
                  <span className="admin-metrics-alert-kind">{a.kind === 'drift' ? 'History drift' : 'Large edit impact'}</span>
                  <span className="admin-metrics-alert-date">{a.alert_date}</span>
                  <p>{alertHeadline(a)}</p>
                </div>
                <button type="button" className="admin-feedback-bulk-btn" onClick={() => ackAlert(a.id)}>
                  Acknowledge
                </button>
              </div>
            ))}
          </div>
        )}

        {loading ? (
          <p className="admin-feedback-loading">Loading snapshots…</p>
        ) : snapshots.length === 0 ? (
          <p className="admin-feedback-empty">
            No snapshots yet for this strategy. They accumulate daily at 5:30 PM ET — or click “Snapshot now”.
          </p>
        ) : (
          <>
            <div className="admin-metrics-diff-controls">
              <span>Compare</span>
              <select className="admin-feedback-select" value={diffFrom} onChange={(e) => setDiffFrom(e.target.value)}>
                <option value="">From date…</option>
                {snapshotDates.map((d) => <option key={`f-${d}`} value={d}>{d}</option>)}
              </select>
              <span>to</span>
              <select className="admin-feedback-select" value={diffTo} onChange={(e) => setDiffTo(e.target.value)}>
                <option value="">To date…</option>
                {snapshotDates.map((d) => <option key={`t-${d}`} value={d}>{d}</option>)}
              </select>
              <button
                type="button"
                className="admin-feedback-bulk-btn"
                onClick={loadDiff}
                disabled={!diffFrom || !diffTo || diffLoading}
              >
                {diffLoading ? 'Comparing…' : 'Compare'}
              </button>
            </div>

            {diff && (
              <div className="admin-metrics-diff">
                <h2>{activeStrategyName}: {diff.from} → {diff.to} ({WINDOW_LABELS[windowKey]})</h2>
                <div className="admin-feedback-table-wrap">
                  <table className="admin-feedback-table admin-metrics-table admin-metrics-diff-table">
                    <thead>
                      <tr><th>Metric</th><th className="num">{diff.from}</th><th className="num">{diff.to}</th><th className="num">Δ Change</th></tr>
                    </thead>
                    <tbody>
                      {renderDiffRow('Total return (fixed)', (r) => r.metrics?.fixedReturnPct)}
                      {renderDiffRow('Total return (compounded)', (r) => r.metrics?.compoundedReturnPct)}
                      {renderDiffRow('Net P&L', (r) => r.metrics?.netPnl, fmtMoney)}
                      {renderDiffRow('Max drawdown', (r) => r.metrics?.maxDrawdownPct)}
                      {renderDiffRow('Win rate', (r) => r.metrics?.winRatePct)}
                      {renderDiffRow('Trades', (r) => r.metrics?.tradeCount, (v) => String(v))}
                      {renderDiffRow('End equity', (r) => r.metrics?.endEquity, fmtMoney)}
                      {renderDiffRow('Data points', (r) => r.provenance?.pointCount, (v) => String(v))}
                    </tbody>
                  </table>
                </div>
                <h3>Admin actions between these dates ({diff.logs.length})</h3>
                {diff.logs.length === 0 ? (
                  <p className="admin-feedback-empty">No logged admin changes touched this strategy in that range.</p>
                ) : (
                  <ul className="admin-metrics-diff-logs">
                    {diff.logs.map((log) => (
                      <li key={log.id}>
                        <span className="admin-metrics-log-when">{fmtDateTime(log.created_at)}</span>
                        <span className="admin-metrics-log-who">{log.actor_name || log.actor_email}</span>
                        <span>{log.summary}</span>
                        {log.metadata?.metricImpact?.largeImpact ? (
                          <span className="admin-metrics-log-flag">⚠ large impact</span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            <p className="admin-feedback-count">{snapshots.length} snapshot{snapshots.length === 1 ? '' : 's'} · {activeStrategyName} · {WINDOW_LABELS[windowKey]}</p>
            <div className="admin-feedback-table-wrap">
              <table className="admin-feedback-table admin-metrics-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th className="num" title="Total return (fixed notional)">Fixed return</th>
                    <th className="num" title="Total return (compounded)">Compounded</th>
                    <th className="num">Net P&L</th>
                    <th className="num">Max DD</th>
                    <th className="num">Win Rate</th>
                    <th className="num">Trades</th>
                    <th>Last Data</th>
                    <th className="num">End Equity</th>
                    <th>Build</th>
                  </tr>
                </thead>
                <tbody>
                  {snapshots.map((s) => (
                    <tr key={s.snapshot_date}>
                      <td>{s.snapshot_date}</td>
                      <td className={`num ${toneClass(s.metrics?.fixedReturnPct)}`}>{fmtPct(s.metrics?.fixedReturnPct)}</td>
                      <td className={`num ${toneClass(s.metrics?.compoundedReturnPct)}`}>{fmtPct(s.metrics?.compoundedReturnPct)}</td>
                      <td className={`num ${toneClass(s.metrics?.netPnl)}`}>{fmtMoney(s.metrics?.netPnl)}</td>
                      <td className="num neg">{fmtPct(s.metrics?.maxDrawdownPct)}</td>
                      <td className="num">{s.metrics?.winRatePct != null ? `${s.metrics.winRatePct}%` : '—'}</td>
                      <td className="num">{s.metrics?.tradeCount ?? '—'}</td>
                      <td>{s.provenance?.lastDataDate || '—'}</td>
                      <td className="num">{fmtMoney(s.metrics?.endEquity)}</td>
                      <td className="admin-metrics-sha">{s.provenance?.gitSha || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default AdminMetricsHistory;
